import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  InboxAdmin,
  InboxAttachmentKind,
  InboxConversation,
  InboxConversationApi,
  InboxFolder,
  InboxMessage,
  InboxMessageApi,
  InboxNotificationItem,
  InboxSearchHit,
} from '../models/inbox.model';
import { decodeInboxReply } from '../../inbox/inbox-reply';

interface ApiSuccess<T> {
  success: boolean;
  data: T;
  message?: string;
  has_more?: boolean;
  unread_count?: number;
}

@Injectable({
  providedIn: 'root',
})
export class InboxService {
  // ==========================================================================
  // PROPRIEDADES
  // ==========================================================================

  private readonly apiUrl = `${environment.apiUrl}/inbox`;

  // ==========================================================================
  // CONSTRUCTOR
  // ==========================================================================

  constructor(private http: HttpClient) {}

  // ==========================================================================
  // MÉTODOS CRUD - CREATE
  // ==========================================================================

  /**
   * Abre ou cria a conversa com um informador ou com o administrador escolhido.
   * @param payload Identificador de quem inicia o fio
   * @returns Conversa pronta para a tela
   */
  public openConversation(payload: { informer_id?: string; admin_id?: string }): Observable<InboxConversation> {
    return this.http.post<ApiSuccess<InboxConversationApi>>(`${this.apiUrl}/conversations`, payload).pipe(
      map((response) => this.toConversation(response.data, [])),
      catchError(this.handleError)
    );
  }

  /**
   * Envia texto e arquivos para a conversa aberta.
   * @param conversationId Identificador da conversa
   * @param body Texto da mensagem
   * @param files Arquivos anexados
   * @returns Mensagem gravada
   */
  public sendMessage(conversationId: string, body: string, files: File[]): Observable<InboxMessage> {
    const form = new FormData();
    if (body.trim()) {
      form.append('body', body.trim());
    }
    files.forEach((file) => form.append('files[]', file, file.name));

    return this.http.post<ApiSuccess<InboxMessageApi>>(`${this.apiUrl}/conversations/${conversationId}/messages`, form).pipe(
      map((response) => this.toMessage(response.data)),
      catchError(this.handleError)
    );
  }

  /**
   * Inclui outro administrador na conversa.
   * @param conversationId Identificador da conversa
   * @param userId Administrador a incluir
   * @returns Conversa atualizada
   */
  public addAdmin(conversationId: string, userId: string): Observable<InboxConversation> {
    return this.http.post<ApiSuccess<InboxConversationApi>>(`${this.apiUrl}/conversations/${conversationId}/participants`, { user_id: userId }).pipe(
      map((response) => this.toConversation(response.data, [])),
      catchError(this.handleError)
    );
  }

  // ==========================================================================
  // MÉTODOS CRUD - READ/LIST
  // ==========================================================================

  /**
   * Lista as conversas visíveis para quem está logado.
   * @param folder Pasta da sidebar
   * @param search Nome buscado na lista
   * @returns Conversas da pasta
   */
  public getConversations(folder: InboxFolder, search = ''): Observable<InboxConversation[]> {
    let params = new HttpParams().set('folder', folder);
    if (search.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http.get<ApiSuccess<InboxConversationApi[]>>(`${this.apiUrl}/conversations`, { params }).pipe(
      map((response) => (response.data ?? []).map((item) => this.toConversation(item, []))),
      catchError(this.handleError)
    );
  }

  /**
   * Lista os administradores que o informador pode escolher.
   * @returns Administradores ativos
   */
  public getAdmins(): Observable<InboxAdmin[]> {
    return this.http.get<ApiSuccess<InboxAdmin[]>>(`${this.apiUrl}/admins`).pipe(
      map((response) => response.data ?? []),
      catchError(this.handleError)
    );
  }

  /**
   * Carrega as mensagens visíveis da conversa.
   * @param conversationId Identificador da conversa
   * @param before Mensagem anterior, para paginar
   * @param around Mensagem que a busca precisa mostrar
   * @returns Mensagens e indicador de página anterior
   */
  public getMessages(conversationId: string, before?: string, around?: string): Observable<{ messages: InboxMessage[]; hasMore: boolean }> {
    let params = new HttpParams().set('per_page', '40');
    if (before) {
      params = params.set('before', before);
    }
    if (around) {
      params = params.set('around', around);
    }

    return this.http.get<ApiSuccess<InboxMessageApi[]>>(`${this.apiUrl}/conversations/${conversationId}/messages`, { params }).pipe(
      map((response) => ({
        messages: (response.data ?? []).map((item) => this.toMessage(item)),
        hasMore: response.has_more === true,
      })),
      catchError(this.handleError)
    );
  }

  /**
   * Busca uma palavra no histórico visível da conversa.
   * @param conversationId Identificador da conversa
   * @param term Palavra digitada
   * @returns Ocorrências em ordem
   */
  public searchMessages(conversationId: string, term: string): Observable<InboxSearchHit[]> {
    const params = new HttpParams().set('q', term);
    return this.http.get<ApiSuccess<InboxSearchHit[]>>(`${this.apiUrl}/conversations/${conversationId}/messages/search`, { params }).pipe(
      map((response) => response.data ?? []),
      catchError(this.handleError)
    );
  }

  /**
   * Lista as notificações de mensagem nova.
   * @returns Itens do sino e a quantidade não lida
   */
  public getNotifications(): Observable<{ items: InboxNotificationItem[]; unreadCount: number }> {
    return this.http.get<ApiSuccess<InboxNotificationItem[]>>(`${this.apiUrl}/notifications`).pipe(
      map((response) => ({
        items: response.data ?? [],
        unreadCount: response.unread_count ?? 0,
      })),
      catchError(this.handleError)
    );
  }

  /**
   * Baixa um anexo enviado no chat.
   * @param attachmentId Identificador do arquivo
   * @returns Conteúdo do arquivo
   */
  public downloadAttachment(attachmentId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/attachments/${attachmentId}/download`, { responseType: 'blob' }).pipe(
      catchError(this.handleError)
    );
  }

  // ==========================================================================
  // MÉTODOS CRUD - UPDATE
  // ==========================================================================

  /**
   * Edita o texto de uma mensagem enviada.
   * @param conversationId Identificador da conversa
   * @param messageId Identificador da mensagem
   * @param body Novo texto
   * @returns Mensagem editada
   */
  public updateMessage(conversationId: string, messageId: string, body: string): Observable<InboxMessage> {
    return this.http.patch<ApiSuccess<InboxMessageApi>>(`${this.apiUrl}/conversations/${conversationId}/messages/${messageId}`, { body }).pipe(
      map((response) => this.toMessage(response.data)),
      catchError(this.handleError)
    );
  }

  /**
   * Alterna a reação do usuário na mensagem (mesmo emoji remove).
   * @param conversationId Identificador da conversa
   * @param messageId Identificador da mensagem
   * @param emoji Emoji da reação
   * @returns Mensagem atualizada
   */
  public reactToMessage(conversationId: string, messageId: string, emoji: string): Observable<InboxMessage> {
    return this.http
      .post<ApiSuccess<InboxMessageApi>>(`${this.apiUrl}/conversations/${conversationId}/messages/${messageId}/reactions`, { emoji })
      .pipe(
        map((response) => this.toMessage(response.data)),
        catchError(this.handleError)
      );
  }

  /**
   * Marca a conversa como lida para quem abriu.
   * @param conversationId Identificador da conversa
   */
  public markRead(conversationId: string): Observable<void> {
    return this.http.post<ApiSuccess<null>>(`${this.apiUrl}/conversations/${conversationId}/read`, {}).pipe(
      map(() => undefined),
      catchError(this.handleError)
    );
  }

  /**
   * Marca ou desmarca a conversa como favorita para quem clicou.
   * @param conversationId Identificador da conversa
   */
  public toggleFavorite(conversationId: string): Observable<boolean> {
    return this.http.post<ApiSuccess<{ favorite: boolean }>>(`${this.apiUrl}/conversations/${conversationId}/favorite`, {}).pipe(
      map((response) => response.data.favorite),
      catchError(this.handleError)
    );
  }

  /**
   * Arquiva ou desarquiva a conversa para quem clicou.
   * @param conversationId Identificador da conversa
   */
  public toggleArchive(conversationId: string): Observable<boolean> {
    return this.http.post<ApiSuccess<{ archived: boolean }>>(`${this.apiUrl}/conversations/${conversationId}/archive`, {}).pipe(
      map((response) => response.data.archived),
      catchError(this.handleError)
    );
  }

  /**
   * Marca a conversa como não lida para quem clicou.
   * @param conversationId Identificador da conversa
   */
  public markUnread(conversationId: string): Observable<void> {
    return this.http.post<ApiSuccess<null>>(`${this.apiUrl}/conversations/${conversationId}/unread`, {}).pipe(
      map(() => undefined),
      catchError(this.handleError)
    );
  }

  /**
   * Informa que o usuário está com o chat aberto.
   */
  public touchPresence(): Observable<void> {
    return this.http.post<ApiSuccess<null>>(`${this.apiUrl}/presence`, {}).pipe(
      map(() => undefined),
      catchError(this.handleError)
    );
  }

  /**
   * Marca uma notificação do sino como lida.
   * @param notificationId Identificador da notificação
   */
  public markNotificationRead(notificationId: string): Observable<void> {
    return this.http.post<ApiSuccess<null>>(`${this.apiUrl}/notifications/${notificationId}/read`, {}).pipe(
      map(() => undefined),
      catchError(this.handleError)
    );
  }

  // ==========================================================================
  // MÉTODOS CRUD - DELETE
  // ==========================================================================

  /**
   * Limpa o histórico apenas para quem confirmou.
   * @param conversationId Identificador da conversa
   */
  public clearConversation(conversationId: string): Observable<void> {
    return this.http.post<ApiSuccess<null>>(`${this.apiUrl}/conversations/${conversationId}/clear`, {}).pipe(
      map(() => undefined),
      catchError(this.handleError)
    );
  }

  /**
   * Tira a conversa da lista apenas para quem confirmou.
   * @param conversationId Identificador da conversa
   */
  public hideConversation(conversationId: string): Observable<void> {
    return this.http.post<ApiSuccess<null>>(`${this.apiUrl}/conversations/${conversationId}/hide`, {}).pipe(
      map(() => undefined),
      catchError(this.handleError)
    );
  }

  /**
   * Apaga a mensagem só para quem pediu.
   * @param conversationId Identificador da conversa
   * @param messageId Identificador da mensagem
   */
  public deleteMessageForMe(conversationId: string, messageId: string): Observable<void> {
    const params = new HttpParams().set('scope', 'me');
    return this.http.delete<ApiSuccess<null>>(`${this.apiUrl}/conversations/${conversationId}/messages/${messageId}`, { params }).pipe(
      map(() => undefined),
      catchError(this.handleError)
    );
  }

  /**
   * Apaga a mensagem para todos os participantes.
   * @param conversationId Identificador da conversa
   * @param messageId Identificador da mensagem
   * @returns Mensagem com o aviso de exclusão
   */
  public deleteMessageForEveryone(conversationId: string, messageId: string): Observable<InboxMessage> {
    const params = new HttpParams().set('scope', 'everyone');
    return this.http.delete<ApiSuccess<InboxMessageApi>>(`${this.apiUrl}/conversations/${conversationId}/messages/${messageId}`, { params }).pipe(
      map((response) => this.toMessage(response.data)),
      catchError(this.handleError)
    );
  }

  /**
   * Retira um administrador da conversa.
   * @param conversationId Identificador da conversa
   * @param userId Administrador retirado
   */
  public removeAdmin(conversationId: string, userId: string): Observable<InboxConversation> {
    return this.http.delete<ApiSuccess<InboxConversationApi>>(`${this.apiUrl}/conversations/${conversationId}/participants/${userId}`).pipe(
      map((response) => this.toConversation(response.data, [])),
      catchError(this.handleError)
    );
  }

  /**
   * O próprio administrador sai da conversa.
   * @param conversationId Identificador da conversa
   */
  public leaveConversation(conversationId: string): Observable<void> {
    return this.http.post<ApiSuccess<null>>(`${this.apiUrl}/conversations/${conversationId}/leave`, {}).pipe(
      map(() => undefined),
      catchError(this.handleError)
    );
  }

  // ==========================================================================
  // MÉTODOS PRIVADOS
  // ==========================================================================

  private toConversation(item: InboxConversationApi, messages: InboxMessage[]): InboxConversation {
    return {
      id: item.id,
      title: item.title,
      initials: item.initials || 'N',
      color: this.colorFromId(item.informer_user_id || item.id),
      avatar: 'initials',
      diocese: item.diocese ?? '',
      state: item.state ?? '',
      email: item.email ?? '',
      preview: item.preview ?? '',
      timeLabel: item.time_label ?? '',
      unread: item.unread > 0 ? item.unread : undefined,
      favorite: item.favorite,
      archived: item.archived,
      online: item.online,
      messages,
      informerUserId: item.informer_user_id,
      admins: item.admins ?? [],
    };
  }

  private toMessage(item: InboxMessageApi): InboxMessage {
    const deletedText = item.deleted ? 'Mensagem apagada' : undefined;
    const decoded = decodeInboxReply(item.text);
    const mainText = decoded.text || undefined;

    return {
      id: item.id,
      side: item.side,
      kind: item.attachments?.length ? 'file' : 'text',
      type: item.type,
      text: item.deleted ? deletedText : item.type === 'system' ? item.text ?? undefined : mainText,
      reply: item.deleted || item.type === 'system' ? null : decoded.reply,
      senderName: item.sender?.name,
      edited: item.edited,
      deleted: item.deleted,
      receipt: item.receipt,
      reactions: (item.reactions ?? []).map((reaction) => ({
        emoji: reaction.emoji,
        count: reaction.count,
        mine: reaction.mine,
      })),
      time: item.time,
      createdAt: item.created_at ?? undefined,
      attachments: (item.attachments ?? []).map((attachment) => ({
        id: attachment.id,
        name: attachment.name,
        kind: this.attachmentKind(attachment.kind, attachment.extension),
        extension: attachment.extension,
        textPreview: null,
        previewUrl: null,
        mime: attachment.mime,
      })),
    };
  }

  private attachmentKind(kind: 'image' | 'audio' | 'document', extension: string): InboxAttachmentKind {
    if (kind === 'image') {
      return 'image';
    }
    if (kind === 'audio' || ['webm', 'ogg', 'mp3', 'wav', 'm4a', 'aac', 'opus'].includes(extension)) {
      return 'audio';
    }
    if (extension === 'pdf') {
      return 'pdf';
    }
    if (['xls', 'xlsx', 'csv', 'ods'].includes(extension)) {
      return 'spreadsheet';
    }
    if (['doc', 'docx'].includes(extension)) {
      return 'document';
    }
    if (['ppt', 'pptx'].includes(extension)) {
      return 'presentation';
    }
    if (['txt', 'md'].includes(extension)) {
      return 'text';
    }
    return 'file';
  }

  private colorFromId(id: string): string {
    const palette = ['#64748b', '#7c3aed', '#0f766e', '#b45309', '#1d4ed8', '#be123c'];
    let hash = 0;
    for (const char of id) {
      hash = (hash + char.charCodeAt(0)) % palette.length;
    }
    return palette[hash];
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let errorMessage = 'Erro ao processar o chat';
    if (error.error && typeof error.error === 'object' && 'message' in error.error) {
      errorMessage = String(error.error.message);
    } else if (error.status === 403) {
      errorMessage = 'Acesso negado';
    } else if (error.status === 404) {
      errorMessage = 'Conversa não encontrada';
    } else if (error.status === 422) {
      errorMessage = 'Dados inválidos';
    }

    return throwError(() => new Error(errorMessage));
  };
}
